<template>
  <Popover 
    :referenceArrow="false" 
    placement="bottom-end"
    popperClass="header-search-popper"
    trigger="manual"
    v-model="visible"
    :offset="15"
  >
    <Input @focus="inputFocus" @clear="inputFocus" v-model="inputVal" :placeholder="$t('other_search')" clearable>
      <template #prefix>
        <SvgIcon name="search" />
      </template>
    </Input>

    <template slot="popper">
      <Scrollbar maxHeight="6.3rem" always>
        <div class="search-wrapper">

          <div v-if="inputVal">
            <p class="trade-title">{{ $t('trade') }}</p>

            <template v-if="searchList.length">
              <div 
                v-for="item in searchList"
                :key="item.symbol + item.type"
                class="search-item" 
                @click="toTradePage(item, true)"
              >
                <div class="search-item-info">
                  <p v-html="getSearchSymbolDom(item.symbol2)"></p>
                  <p class="search-item-info-type">{{ item.showType }}</p>
                </div>

                <p class="search-item-price">{{ item.price }}</p>

                <div class="search-item-right">
                  <p :class="getColor(item.rate)">{{ numTrim(item.rate, 2) }}%</p>
                  <SymbolFavorite :type="item.type" :symbol="item.symbol" :id="item.id" />
                </div>
              </div>
            </template>

            <no-result v-else></no-result>
          </div>

          <template v-else>
            <div class="search-history" v-if="historyList.length">
              <div class="search-history-head">
                <p>{{ $t('history') }}</p>
                <p @click="clear">{{ $t('clear') }}</p>
              </div>

              <Space :size="[14, 8]" class="search-history-list">
                <template v-for="item in historyList">
                  <div 
                    class="search-history-item" 
                    :key="item"
                    @click="historyItemClick(item)"
                  >
                    {{ item }}
                  </div>
                </template>
              </Space>
            </div>

            <div class="search-history-tabs">
              <div 
                class="search-history-tabs-item"
                :class="{ 'is_active': type === 'search' }"
                @click="type = 'search'"
              >
                {{ $t('top_searches') }}
              </div>

              <div 
                class="search-history-tabs-item"
                :class="{ 'is_active': type === 'events' }"
                @click="type = 'events'"
              >
                {{ $t('hot_events') }}
              </div>
            </div>

            <div class="top-search-wrapper" v-if="type === 'search'">
              <div 
                class="top-search-item"
                v-for="(item, i) in topList" 
                :key="item.symbol + item.type"
                @click="toTradePage(item)"
              >
                <p class="rank">{{ i + 1 }}.</p>
                <div class="top-search-item-info">
                  <p>{{ item.symbol2 }}</p>
                  <p class="top-search-item-info-type">
                    {{ item.showType }}
 
                    
                    <template v-if="i < 3">
                      <SvgIcon name="hot" v-for="k in (3 - i)" :key="k" />
                    </template>
                  </p>
                </div>

                <p class="top-search-item-price">{{ item.price }}</p>

                <div class="top-search-item-right">
                  <p :class="getColor(item.rate)">{{ numTrim(item.rate, 2) }}%</p>
                  <SymbolFavorite :type="item.type" :symbol="item.symbol" :id="item.id" />
                </div>
              </div>
            </div>

            <div class="hot-events" v-if="type === 'events'">
              <a 
                class="hot-events-item" 
                v-for="(item, i) in hotEventList" 
                :key="item.id"
                :href="item.url"
                target="_blank"
              >
                <p class="rank">{{ i + 1 }}.</p>

                <div class="hot-events-item-content">
                  <p class="title">{{ item.title }}</p>
                  <p class="desc">{{ item.sub_title }}</p>
                </div>

                <SvgIcon name="arrow-right" />
              </a>
            </div>
          </template> 
        </div>
      </Scrollbar>
    </template>
  </Popover>
</template>

<script>
import Popover from 'components/common/popover'
import SymbolFavorite from 'components/client/symbol-favorite.vue'
import { getTopSearch, setTopSearch, getHotEvent } from 'data/api/common'
import StoreData from 'data/storage'
import { mapGetters, mapState, mapActions } from 'vuex'

export default {
  components: {
    Popover,
    SymbolFavorite
  },
  data() {
    return {
      visible: false,
      inputVal: '',
      type: 'search',
      historyList: StoreData.getHeaderSearchRecords(),
      topSearchList: [],
      hotEventList: []
    }
  },
  computed: {
    ...mapState('user', ['info']),
    ...mapState('market', ['symbols']),
    ...mapState('futures', ['futuresSymbols']),
    ...mapGetters('market', ['symbolsArr']),
    ...mapGetters('futures', ['futuresSymbolList']),
    // symbol 传值， symbol2 显示
    searchList() {
      const searchVal = this.inputVal.toUpperCase()

      const futures = this.futuresSymbolList
        .filter(item => item.symbol.includes(searchVal))
        .map(item => ({
          type: 'futures',
          showType: this.$t('Futures'),
          symbol: item.symbol,
          symbol2: item.symbolName,
          coin: item.base,
          price: this.c2ThousandsCount(this.numTrim(item.ticker.curPrice, item.precision.price)),
          rate: item.ticker.riseFallRate * 100
        }))

      const spot = this.symbolsArr
        .filter(item => item.symbol1.includes(searchVal))
        .map(item => ({
          id: item.ID,
          type: 'spot',
          showType: this.$t('spot'),
          symbol: item.symbol,
          symbol2: item.symbol1,
          coin: item.base_asset,
          price: this.c2ThousandsCount(this.numTrim(item.last, item.precision.price)),
          rate: item.rate
        }))

      return futures.concat(spot)
    },
    topList() {
      return this.topSearchList.map(item => {
        return {
          symbol: item.symbol,
          type: item.type,
          showType: item.type === 'spot' ? this.$t('spot') : this.$t('Futures'),
          ...this.getTopSearchSymbolData(item)
        }
      })
    }
  },
  watch: {
    'info.id': {
      handler() {
        this.initSpotFavorite()
        this.initFuturesFavorite()
      },
      immediate: true
    },
    '$route'() {
      this.visible = false
    }
  },
  methods: {
    ...mapActions('futures', ['getFuturesSymbols', 'initFuturesFavorite']),
    ...mapActions('market', ['getSymbols', 'listenPrices', 'updateSymbol', 'initSpotFavorite']),
    inputFocus() {
      setTimeout(() => {
        this.visible = true 
      });
    },
    clear() {
      StoreData.clearHeaderSearchRecords()
      this.historyList = []
      this.inputVal = ''
    },
    historyItemClick(val) {
      this.inputVal = val

      StoreData.setHeaderSearchRecords(val)
      this.historyList = StoreData.getHeaderSearchRecords()
    },
    getSearchSymbolDom(symbol) {
      const searchVal = this.inputVal.toUpperCase()

      return symbol.replace(searchVal, `<span>${searchVal}</span>`)
    },
    toTradePage(data, isSearch = false) {
      if (isSearch) {
        StoreData.setHeaderSearchRecords(data.coin)
        this.historyList = StoreData.getHeaderSearchRecords()

        setTopSearch({
          symbol: data.symbol,
          type: data.type
        })
      }

      this.inputVal = ''

      if (data.type === 'spot') {
        window.open(`/exchange/${data.symbol}`)
      } else  {
        window.open(`/futures/${data.symbol2}`)
      }
    },
    // topSearch
    getTopSearchSymbolData(data) {
      let obj = {
        symbol2: '',
        coin: '',
        price: '--',
        rate: 0,
      }

      if (data.type === 'spot') {
        const symbolData = this.symbols[data.symbol]
        if (symbolData) {

          obj = {
            id: symbolData.ID,
            symbol2: symbolData.symbol1,
            coin: symbolData.base_asset,
            price: this.c2ThousandsCount(this.numTrim(symbolData.last, symbolData.precision.price)),
            rate: symbolData.rate
          }
        }
      } else {
        const symbolData = this.futuresSymbols[data.symbol]
        if (symbolData) {

          obj = {
            symbol2: symbolData.symbolName,
            coin: symbolData.base,
            price: this.c2ThousandsCount(this.numTrim(symbolData.ticker.curPrice, symbolData.precision.price)),
            rate: symbolData.ticker.riseFallRate * 100
          }
        }
      }

      return obj
    }
  },
  created() {
    getTopSearch().then(({ data }) => {
      this.topSearchList = data || []
    })
    
    getHotEvent().then(({ data }) => {
      this.hotEventList = data || []
    })

    this.getFuturesSymbols()
    this.getSymbols().then(() => {
      this.listenPrices()
    })
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>

<style lang="scss">
.header-search-popper {
  z-index: $header-search-popper-index;
}
</style>